import { CalendarDays, MapPin, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AccountEventsLayout } from '@/layouts/AccountEventsLayout';
import { conclaveApi, type ConclaveEvent } from '@/lib/conclave-api';
import { conclaveEventPath } from '@/lib/conclave-paths';
import { Loader2 } from 'lucide-react';

export default function MyConclaveEventsPage() {
  const { data, isLoading } = useSWR('conclave-events', () => conclaveApi.listConclaveEvents());
  const events = data?.events ?? [];

  return (
    <AccountEventsLayout>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Networking events</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Conclaves you created, registered for, or can join — no community required.
          </p>
        </div>
        <Button className="rounded-xl shrink-0" asChild>
          <Link to="/events/create">
            <Plus className="mr-2 h-4 w-4" />
            Create
          </Link>
        </Button>
      </div>

      <div className="mt-6 space-y-3">
        {isLoading && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {!isLoading && !events.length && (
          <Card className="glass-card">
            <CardContent className="py-10 text-center text-muted-foreground">
              No conclave events yet. Create one or open a shared event link to register.
            </CardContent>
          </Card>
        )}

        {events.map((event: ConclaveEvent) => (
          <Link
            key={event.id}
            to={conclaveEventPath(event.slug || event.id, event.communityId)}
            className="block"
          >
            <Card className="glass-card transition-shadow hover:shadow-md">
              <CardContent className="py-4">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="font-medium text-foreground">{event.title}</h2>
                  <div className="flex flex-wrap justify-end gap-2">
                    {event.standalone && (
                      <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                        Open event
                      </span>
                    )}
                    {event.isAdmin && (
                      <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                        Organiser
                      </span>
                    )}
                    {event.isRegistered && (
                      <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700">
                        Registered
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarDays className="h-4 w-4" />
                    {new Date(event.startDate).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                  {event.address && (
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-4 w-4" />
                      {event.address}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </AccountEventsLayout>
  );
}
